import React, { useState, useCallback, useEffect } from 'react';

import {
  Box,
  Paper,
  TextField,
  Grid,
  Switch,
  Select,
  FormControlLabel,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  styled,
  Divider,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { checkValueExists } from '../../../utils/dataSchema';
import CommonSectionHeader from '../../common/CommonSectionHeader';
import CommonRestricted from '../../common/CommonRestricted';

import { useRegions, regionTypes } from '../../../hooks/api/useRegions';
import { useBranches, branchTypes, targetTypes } from '../../../hooks/api/useBranches';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useAppContextFeatures } from '../../../context/AppContext';
import { initialValues, validationSchema, retailTypes } from '../../../schema/users/branchSchema';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

const StyledCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function UpdateOutlet({ id, routeType, handleOnSuccess }) {
  const { t } = useTranslations({ ns: ['common', '@user'] });
  // const { retailerId } = useParams();

  const { promptMessage } = useSystemPrompt();
  const { isLoading, get, update } = useBranches({ branchType: branchTypes.retail, targetType: targetTypes.retail });
  const { isPermissionAllow } = usePermissions(
    routeType,
    scopeIds.userBranches.id,
    scopeTypes[scopeIds.userBranches.id].retail.id
  );

  const { setSystemLoading } = useAppContextFeatures();

  const navigate = useNavigate();
  const [savedValues, setSavedValues] = useState();
  const { getRoute } = useSystemRoutes(routeTypes.admin);

  const { list: listRegions } = useRegions({});
  const [dataCountries, setDataCountries] = useState([]);
  const [dataStates, setDataStates] = useState([]);
  const [dataAreas, setDataAreas] = useState([]);

  const loadData = useCallback(
    async (id) => {
      setSystemLoading(true);

      try {
        const { data } = await get({ id });
        const { branch: outlet } = data;

        setSavedValues({
          branchName: outlet.branch_name,
          branchCode: outlet.branch_code,
          branchContact: outlet.branch_contact,
          branchEmail: outlet.branch_email,
          type: outlet.type,
          address1: outlet.address_1,
          address2: outlet.address_2 ? outlet.address_2 : '',
          countryId: outlet.country_id,
          stateId: outlet.state_id,
          areaId: outlet.area_id,
          postcode: outlet.postcode,
          branchRemark: outlet.remark ? outlet.remark : '',
          picName: outlet.pic_name ? outlet.pic_name : '',
          picContact: outlet.pic_contact ? outlet.pic_contact : '',
          picEmail: outlet.pic_email ? outlet.pic_email : '',
          isHQ: outlet?.is_hq === '1',
          userId: outlet?.user_id,
          id: outlet?.id,
          status: outlet?.status === '1',
        });
      } catch (error) {
        promptMessage(false, `${error?.message}`);
      }

      setSystemLoading(false);
    },
    [id]
  );

  const onSubmit = async (currentValues) => {
    try {
      let data = {
        branch: {
          branch_name: currentValues?.branchName,
          branch_code: currentValues?.branchCode,
          branch_contact: currentValues?.branchContact,
          branch_email: currentValues?.branchEmail,
          type: currentValues?.type,
          address_1: currentValues?.address1,
          address_2: currentValues?.address2,
          country_id: currentValues?.countryId,
          state_id: currentValues?.stateId,
          area_id: currentValues?.areaId,
          postcode: currentValues?.postcode,
          remark: currentValues?.branchRemark,
          pic_name: currentValues?.picName,
          pic_contact: currentValues?.picContact,
          pic_email: currentValues?.picEmail,
          is_hq: currentValues?.isHQ ? 1 : 0,
          status: currentValues?.status ? 1 : 0,
        },
        props: {
          marking: currentValues?.marking ? currentValues.marking : '',
        },
      };

      const response = await update({
        userId: currentValues.userId,
        branchId: currentValues.id,
        data,
      });

      promptMessage(true, `${response?.message}`);
      // navigate(getRoute(`users/retailer/${retailerId}/retailerOutlets`))
      if (handleOnSuccess) handleOnSuccess();
    } catch (error) {
      promptMessage(false, `Action Failed. (${error?.message})`);
    }
  };

  const fetchDataCountry = async () => {
    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'country_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.country;

      const { data } = await listRegions(props);

      setDataCountries(data?.countries);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataStates = async (countryId) => {
    setDataStates([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'state_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.state;
      props.countryId = countryId;

      const { data } = await listRegions(props);

      setDataStates(data?.states);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataAreas = async (stateId) => {
    setDataAreas([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'area_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.area;
      props.stateId = stateId;

      const { data } = await listRegions(props);

      setDataAreas(data?.areas);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const formik = useFormik({
    initialValues: savedValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit,
  });

  const { values, errors, touched, handleChange, setFieldValue } = formik;

  useEffect(() => {
    loadData(id);
    fetchDataCountry();
  }, []);

  useEffect(() => {
    if (formik?.values?.countryId) {
      if (dataStates.length) {
        if (dataStates[0].country_id !== formik.values.countryId) {
          fetchDataStates(formik.values.countryId);
        }
      } else {
        fetchDataStates(formik.values.countryId);
      }
    }

    if (formik?.values?.stateId) {
      if (dataAreas.length) {
        if (dataAreas[0].state_id !== formik.values.stateId) {
          fetchDataAreas(formik.values.stateId);
        }
      } else {
        fetchDataAreas(formik.values.stateId);
      }
    }
  }, [formik.values]);

  const checkPostcodeExists = () => {
    const checkExists = getPostcodesArray().find((i) => i === values.postcode);

    if (checkExists) return values.postcode;

    return '';
  };

  const getPostcodesArray = () => {
    let postcodeStringCheck = dataAreas.filter((i) => i.id === formik.values.areaId);

    if (postcodeStringCheck.length <= 0) return [];

    let postcodeString = postcodeStringCheck.length >= 0 ? postcodeStringCheck[0].postcode_string : '';

    let postcodeArray = postcodeString.includes(',') ? postcodeString.split(',') : [postcodeString];

    if (postcodeArray.length <= 0) return [];

    return postcodeArray;
  };

  const RenderForm = () => {
    if (!values) return <></>;

    return (
      <form onSubmit={formik.handleSubmit}>
        <StyledCard>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6} textAlign={'left'}>
              <CommonSectionHeader title={t('outletInfo')} subTitle={t('fullDetailsOfOutletInformation')} />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    name="status"
                    checked={Boolean(values.status)}
                    onChange={(event, checked) => {
                      setFieldValue('status', checked);
                    }}
                  />
                }
                label={Boolean(values.status) ? t('enabled') : t('disabled')}
              />
              <></>
            </Grid>

            <Grid item xs={12} sm={6} md={4} textAlign="right">
              <FormControlLabel
                control={
                  <Switch
                    name="isHQ"
                    checked={Boolean(values?.isHQ)}
                    onChange={(event, checked) => {
                      setFieldValue('isHQ', checked);
                    }}
                  />
                }
                label={Boolean(values.isHQ) ? t('mainOutlet') : t('normalOutlet')}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    autoFocus
                    fullWidth
                    name={'branchName'}
                    label={t('outletName')}
                    type="text"
                    variant="outlined"
                    value={values?.branchName}
                    onChange={handleChange}
                    error={touched.branchName && Boolean(errors.branchName)}
                    helperText={touched.branchName && errors.branchName}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'branchCode'}
                    label={t('outletCode')}
                    type="text"
                    variant="outlined"
                    value={values?.branchCode}
                    onChange={handleChange}
                    error={touched.branchCode && Boolean(errors.branchCode)}
                    helperText={touched.branchCode && errors.branchCode}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'branchContact'}
                    label={t('outletContact')}
                    type="text"
                    variant="outlined"
                    value={values?.branchContact}
                    onChange={handleChange}
                    error={touched.branchContact && Boolean(errors.branchContact)}
                    helperText={touched.branchContact && errors.branchContact}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'branchEmail'}
                    label={t('outletEmail')}
                    type="text"
                    variant="outlined"
                    value={values?.branchEmail}
                    onChange={handleChange}
                    error={touched.branchEmail && Boolean(errors.branchEmail)}
                    helperText={touched.branchEmail && errors.branchEmail}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'picName'}
                    label={t('contactName')}
                    type="text"
                    variant="outlined"
                    value={values?.picName}
                    onChange={handleChange}
                    error={touched.picName && Boolean(errors.picName)}
                    helperText={touched.picName && errors.picName}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'picContact'}
                    label={t('contactNo')}
                    type="text"
                    variant="outlined"
                    value={values?.picContact}
                    onChange={handleChange}
                    error={touched.picContact && Boolean(errors.picContact)}
                    helperText={touched.picContact && errors.picContact}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'picEmail'}
                    label={t('contactEmail')}
                    type="text"
                    variant="outlined"
                    value={values?.picEmail}
                    onChange={handleChange}
                    error={touched.picEmail && Boolean(errors.picEmail)}
                    helperText={touched.picEmail && errors.picEmail}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'branchRemark'}
                    label={t('remark')}
                    type="text"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={values?.branchRemark ? values.branchRemark : ''}
                    onChange={handleChange}
                    error={touched.branchRemark && Boolean(errors.branchRemark)}
                    helperText={touched.branchRemark && errors.branchRemark}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={6} textAlign={'left'}>
                  <CommonSectionHeader title={t('outletAddress')} subTitle={t('fullOutletAddressDetailsInformation')} />
                </Grid>

                <Grid item xs={12} sm={12} md={6}>
                  <FormControl margin="dense" fullWidth size="small" error={touched.type && Boolean(errors.type)}>
                    <InputLabel id="type">{t('outletType')}</InputLabel>
                    <Select
                      disabled
                      labelId="type"
                      label={t('outletType')}
                      name={'type'}
                      onChange={(event) => {
                        setFieldValue('type', event.target.value);
                      }}
                      value={values.type}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {retailTypes.map((i, index) => {
                        return (
                          <MenuItem key={index} value={i.id}>
                            {i.display}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>{touched.type && errors.type}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'address1'}
                    label={t('address1')}
                    type="text"
                    variant="outlined"
                    value={values?.address1}
                    onChange={handleChange}
                    error={touched.address1 && Boolean(errors.address1)}
                    helperText={touched.address1 && errors.address1}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'address2'}
                    label={t('address2')}
                    type="text"
                    variant="outlined"
                    value={values?.address2}
                    onChange={handleChange}
                    error={touched.address2 && Boolean(errors.address2)}
                    helperText={touched.address2 && errors.address2}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    error={touched.countryId && Boolean(errors.countryId)}
                  >
                    <InputLabel id="country">{t('country')}</InputLabel>
                    <Select
                      labelId="countryId"
                      label={t('country')}
                      name={'countryId'}
                      onChange={(event) => {
                        setFieldValue(`countryId`, event.target.value);
                        setFieldValue(`stateId`, '');
                        setFieldValue(`areaId`, '');
                        setDataStates([]);
                        setDataAreas([]);
                      }}
                      value={checkValueExists({
                        dataSearch: dataCountries,
                        valueToCompare: values.countryId,
                      })}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {dataCountries.map((i, index) => {
                        return (
                          <MenuItem key={index} value={i.id}>
                            {i.country_name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>{touched.countryId && errors.countryId}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl margin="dense" fullWidth size="small" error={touched.stateId && Boolean(errors.stateId)}>
                    <InputLabel id="state">{t('state')}</InputLabel>
                    <Select
                      labelId="stateId"
                      label={t('state')}
                      name={'stateId'}
                      onChange={(event) => {
                        setFieldValue(`stateId`, event.target.value);
                        setFieldValue(`areaId`, '');
                        setDataAreas([]);
                      }}
                      value={checkValueExists({
                        dataSearch: dataStates,
                        valueToCompare: values.stateId,
                      })}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {dataStates.map((i, index) => {
                        return (
                          <MenuItem key={index} value={i.id}>
                            {i.state_name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>{touched.stateId && errors.stateId}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={12} md={6}>
                  <FormControl margin="dense" fullWidth size="small" error={touched.areaId && Boolean(errors.areaId)}>
                    <InputLabel id="area">{t('area')}</InputLabel>
                    <Select
                      labelId="areaId"
                      label={t('area')}
                      name={'areaId'}
                      onChange={handleChange}
                      value={checkValueExists({
                        dataSearch: dataAreas,
                        valueToCompare: values.areaId,
                      })}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {dataAreas.map((i, index) => {
                        return (
                          <MenuItem key={index} value={i.id}>
                            {i.area_name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>{touched.areaId && errors.areaId}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    error={touched.postcode && Boolean(errors.postcode)}
                  >
                    <InputLabel id="postcode">{t('postcode')}</InputLabel>
                    <Select
                      labelId="postcode"
                      label={t('postcode')}
                      name={'postcode'}
                      onChange={handleChange}
                      value={checkPostcodeExists()}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {getPostcodesArray().length ? (
                        getPostcodesArray().map((i, index) => {
                          return (
                            <MenuItem key={index} value={i}>
                              {i}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem value={''}>{null}</MenuItem>
                      )}
                    </Select>
                    <FormHelperText>{touched.postcode && errors.postcode}</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </StyledCard>
        <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
          <Grid item md={12} textAlign={'right'}>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
              }}
              loading={isLoading}
            >
              {t('saveChanges')}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <Box>{isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ? RenderForm() : <CommonRestricted />}</Box>
  );
}
